import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import { Briefcase, FloppyDisk, Trash, ArrowLeft, MagicWand, ListChecks, LockKey, LockKeyOpen } from "@phosphor-icons/react";

const EMPTY = {
  title: "", department: "", location: "", employment_type: "full-time",
  experience_level: "mid", salary_min: "", salary_max: "", salary_currency: "USD",
  description: "", requirements: "", responsibilities: "", skills: [], benefits: "",
  status: "draft", openings_count: 1, application_email: "", template_id: "",
  published_channels: [],
};

export default function JobEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useCapabilities();
  const { confirm, prompt } = useDialog();
  const [form, setForm] = useState(EMPTY);
  const [templates, setTemplates] = useState([]);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [skillInput, setSkillInput] = useState("");
  const isNew = !id;
  const canEdit = can("manage_jobs");

  useEffect(() => { (async () => {
    try {
      const tr = await api.get("/job-templates");
      setTemplates(tr.data.templates || []);
      if (id) {
        const jr = await api.get(`/jobs/${id}`);
        setForm({ ...EMPTY, ...jr.data,
          salary_min: jr.data.salary_min ?? "", salary_max: jr.data.salary_max ?? "" });
      }
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setLoading(false); }
  })(); /* eslint-disable-next-line */ }, [id]);

  const applyTemplate = (tid) => {
    const t = templates.find(x => x.id === tid);
    if (!t) { setForm({ ...form, template_id: "" }); return; }
    setForm({ ...form,
      template_id: t.id,
      department: form.department || t.department || "",
      description: t.description || form.description,
      requirements: t.requirements || form.requirements,
      responsibilities: t.responsibilities || form.responsibilities,
      skills: (t.skills && t.skills.length) ? t.skills : form.skills,
    });
    setMsg({ type: "ok", text: `Template "${t.name}" applied. Feel free to customize.` });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    if (!form.skills.includes(s)) setForm({ ...form, skills: [...form.skills, s] });
    setSkillInput("");
  };

  const save = async () => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const payload = { ...form };
      payload.salary_min = payload.salary_min === "" ? null : Number(payload.salary_min);
      payload.salary_max = payload.salary_max === "" ? null : Number(payload.salary_max);
      payload.openings_count = Number(payload.openings_count) || 1;
      if (isNew) {
        const r = await api.post("/jobs", payload);
        nav(`/jobs/${r.data.id}`, { replace: true });
      } else {
        await api.patch(`/jobs/${id}`, payload);
      }
      setMsg({ type: "ok", text: "Saved." });
      setTimeout(() => setMsg({ type: "", text: "" }), 4000);
    } catch (e) {
      setMsg({ type: "error", text: formatAxiosError(e) });
    } finally { setSaving(false); }
  };

  const del = async () => {
    const ok = await confirm({
      title: "Delete this job opening?",
      description: "The listing and any applicant links will be permanently removed.",
      confirmText: "Delete job",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/jobs/${id}`); nav("/jobs"); }
    catch (e) { setMsg({ type: "error", text: formatAxiosError(e) }); }
  };

  const closeJob = async () => {
    const reason = await prompt({
      title: "Close this job opening?",
      description: "Optionally record the reason (e.g. filled internally, budget freeze).",
      placeholder: "Reason (optional)",
      confirmText: "Close job",
      cancelText: "Cancel",
      multiline: true,
      rows: 3,
    });
    if (reason === null) return;
    try {
      await api.post(`/jobs/${id}/close`, { reason });
      setForm(f => ({ ...f, status: "closed", closed_reason: reason }));
      setMsg({ type: "ok", text: "Job closed." });
      setTimeout(() => setMsg({ type: "", text: "" }), 3500);
    } catch (e) { setMsg({ type: "error", text: formatAxiosError(e) }); }
  };

  const reopenJob = async () => {
    try {
      await api.post(`/jobs/${id}/reopen`);
      setForm(f => ({ ...f, status: "open" }));
      setMsg({ type: "ok", text: "Job reopened." });
      setTimeout(() => setMsg({ type: "", text: "" }), 3500);
    } catch (e) { setMsg({ type: "error", text: formatAxiosError(e) }); }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <button onClick={() => nav("/jobs")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft size={14} weight="bold" /> Back to Jobs
      </button>
      <div className="mb-6">
        <div className="eyebrow mb-2 flex items-center gap-2"><Briefcase size={12} weight="bold" /> {isNew ? "New job opening" : "Job opening"}</div>
        <h1 className="font-display text-3xl font-black">{isNew ? "Post a new role" : form.title || "Untitled job"}</h1>
      </div>
      {msg.text && (
        <div className={`mb-4 text-xs px-3 py-2 border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      {isNew && canEdit && (
        <div className="mb-6 border border-[#002FA7]/30 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2"><MagicWand size={14} weight="bold" className="text-[#002FA7]" />
            <div className="font-semibold text-sm">Start from a template</div></div>
          <select className="input" value={form.template_id} onChange={(e) => applyTemplate(e.target.value)} data-testid="template-select">
            <option value="">— Blank —</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.department ? ` (${t.department})` : ""}</option>)}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Job title*"><input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} disabled={!canEdit} data-testid="job-title" /></Field>
        <Field label="Department"><input className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} disabled={!canEdit} data-testid="job-dept" /></Field>
        <Field label="Location"><input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} disabled={!canEdit} data-testid="job-location" /></Field>
        <Field label="Employment type">
          <select className="input" value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} disabled={!canEdit}>
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
            <option value="contract">Contract</option>
            <option value="internship">Internship</option>
            <option value="temporary">Temporary</option>
          </select>
        </Field>
        <Field label="Experience level">
          <select className="input" value={form.experience_level} onChange={(e) => setForm({ ...form, experience_level: e.target.value })} disabled={!canEdit}>
            <option value="entry">Entry</option>
            <option value="mid">Mid</option>
            <option value="senior">Senior</option>
            <option value="lead">Lead</option>
            <option value="executive">Executive</option>
          </select>
        </Field>
        <Field label="Status">
          <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={!canEdit}>
            <option value="draft">Draft</option>
            <option value="open">Open (published)</option>
            <option value="on_hold">On hold</option>
            <option value="closed">Closed</option>
          </select>
        </Field>
        <Field label="Salary min"><input className="input" type="number" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} disabled={!canEdit} /></Field>
        <Field label="Salary max"><input className="input" type="number" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} disabled={!canEdit} /></Field>
        <Field label="Currency"><input className="input uppercase" maxLength={8} value={form.salary_currency} onChange={(e) => setForm({ ...form, salary_currency: e.target.value })} disabled={!canEdit} /></Field>
        <Field label="Openings count"><input className="input" type="number" min="1" value={form.openings_count} onChange={(e) => setForm({ ...form, openings_count: e.target.value })} disabled={!canEdit} /></Field>
        <Field label="Application email"><input className="input" type="email" value={form.application_email} onChange={(e) => setForm({ ...form, application_email: e.target.value })} disabled={!canEdit} /></Field>
      </div>

      <div className="mt-4">
        <Field label="Skills">
          <div className="flex flex-wrap gap-1 mb-2">
            {form.skills.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-muted border border-border">
                {s}
                {canEdit && <button onClick={() => setForm({ ...form, skills: form.skills.filter((_, j) => j !== i) })} className="text-red-600">×</button>}
              </span>
            ))}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <input className="input flex-1" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); }}} placeholder="Add a skill and press Enter" data-testid="job-skill-input" />
              <button onClick={addSkill} className="px-3 py-2 border border-border hover:bg-muted text-sm">Add</button>
            </div>
          )}
        </Field>
      </div>

      <Field label="Description"><textarea className="input min-h-[140px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!canEdit} data-testid="job-description" /></Field>
      <Field label="Requirements"><textarea className="input min-h-[140px] font-mono text-xs" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} disabled={!canEdit} /></Field>
      <Field label="Responsibilities"><textarea className="input min-h-[140px] font-mono text-xs" value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} disabled={!canEdit} /></Field>
      <Field label="Benefits (optional)"><textarea className="input min-h-[100px]" value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} disabled={!canEdit} /></Field>

      {canEdit && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-job">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save job"}
          </button>
          {!isNew && (
            <button onClick={() => nav(`/jobs/${id}/applicants`)} className="px-4 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-sm flex items-center gap-2" data-testid="view-applicants">
              <ListChecks size={14} weight="bold" /> View applicants
            </button>
          )}
          {!isNew && form.status !== "closed" && can("close_jobs") && (
            <button onClick={closeJob} className="px-4 py-2 border border-orange-500 text-orange-600 hover:bg-orange-50 text-sm flex items-center gap-2" data-testid="close-job">
              <LockKey size={14} weight="bold" /> Close job
            </button>
          )}
          {!isNew && form.status === "closed" && (
            <button onClick={reopenJob} className="px-4 py-2 border border-green-500 text-green-700 hover:bg-green-50 text-sm flex items-center gap-2" data-testid="reopen-job">
              <LockKeyOpen size={14} weight="bold" /> Reopen job
            </button>
          )}
          {!isNew && (
            <button onClick={del} className="px-4 py-2 border border-red-500 text-red-600 hover:bg-red-50 text-sm flex items-center gap-2">
              <Trash size={14} weight="bold" /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}
